import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import { AgentPageLayout } from '../components/layout/AgentPageLayout';
import { fetchAgentPlayers, fetchShops } from '../services/api';
import type { AgentPlayer, AgentShop } from '../types';
import { ACTION, accountStatus } from '@kioskgaming/ui';

export default function PlayersPage() {
  const [players, setPlayers] = useState<AgentPlayer[]>([]);
  const [shops, setShops] = useState<AgentShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [usernameSearch, setUsernameSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [playerShopFilter, setPlayerShopFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [playersRes, shopsRes] = await Promise.all([fetchAgentPlayers(), fetchShops()]);
      if (playersRes.success && playersRes.data) setPlayers(playersRes.data);
      else toast.error('Could not load Players');
      if (shopsRes.success && shopsRes.data) setShops(shopsRes.data);
    } catch {
      toast.error('Could not load Players');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shopNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of shops) m.set(s.id, s.name);
    return m;
  }, [shops]);

  const filteredPlayers = useMemo(() => {
    const uq = usernameSearch.trim().toLowerCase();
    const pq = phoneSearch.trim().toLowerCase();
    const eq = emailSearch.trim().toLowerCase();
    const digitsOnly = (s: string) => s.replace(/\D/g, '');
    return players.filter((p) => {
      if (playerShopFilter && p.shop_id !== playerShopFilter) return false;
      if (uq && !(p.username || '').toLowerCase().includes(uq)) return false;
      if (pq) {
        const pnorm = digitsOnly(p.phone || '');
        const qnorm = digitsOnly(pq);
        if (!qnorm) {
          if (!(p.phone || '').toLowerCase().includes(pq)) return false;
        } else if (!pnorm.includes(qnorm)) return false;
      }
      if (eq && !(p.email || '').toLowerCase().includes(eq)) return false;
      return true;
    });
  }, [usernameSearch, phoneSearch, emailSearch, playerShopFilter, players]);

  return (
    <AppLayout>
      <AgentPageLayout
        before={(
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm font-medium text-orange-700 hover:text-orange-900"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {ACTION.backToDashboard}
          </Link>
        )}
        title="Players"
        description="All Players assigned to Shops under your account."
      >
        <section className="rounded-xl border border-orange-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-4">
            <div className="mt-0 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm"
                  placeholder="Username"
                  value={usernameSearch}
                  onChange={(e) => setUsernameSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Phone number"
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                inputMode="tel"
                autoComplete="off"
              />
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                placeholder="Email"
                type="search"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                autoComplete="off"
              />
              <select
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm sm:col-span-2 lg:col-span-1"
                value={playerShopFilter}
                onChange={(e) => setPlayerShopFilter(e.target.value)}
              >
                <option value="">All Shops</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">Username</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Shop</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-6" colSpan={5}>
                      <PageLoadingIndicator variant="inline" label="Loading…" />
                    </td>
                  </tr>
                ) : filteredPlayers.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-gray-500" colSpan={5}>
                      No Players found
                    </td>
                  </tr>
                ) : (
                  filteredPlayers.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <Link
                          to={`/players/${p.id}`}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {p.username || '—'}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{p.email || '—'}</td>
                      <td className="px-4 py-3">{p.phone || '—'}</td>
                      <td className="px-4 py-3">
                        {p.shop_id ? (
                          <Link
                            to={`/shops/${p.shop_id}`}
                            className="font-medium text-orange-700 underline-offset-2 hover:text-orange-900 hover:underline"
                          >
                            {shopNameById.get(p.shop_id) || p.shop_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">{accountStatus(p.status).label}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </AgentPageLayout>
    </AppLayout>
  );
}
